import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    question: 'What is Globe Medical?',
    answer:
      'Globe Medical is a digitally connected healthcare platform that brings together verified specialists, organised medical records, and structured guidance within one secure care environment.',
    category: 'General',
  },
  {
    question: 'How do I get started?',
    answer:
      'You begin by logging into your care workspace, uploading relevant medical documents, and selecting the specialist that matches your condition or concern.',
    category: 'General',
  },
  {
    question: 'Can I access the platform from outside my country?',
    answer:
      'Yes. Globe Medical is designed to support individuals across different countries and time zones.',
    category: 'General',
  },
  {
    question: 'Can I reschedule my appointment?',
    answer:
      'Yes. Appointments can be modified within your account based on availability and scheduling policies.',
    category: 'General',
  },
  {
    question: 'Can I choose the specialist I consult with?',
    answer:
      'Yes. You can review available specialists and select one based on expertise, specialty, and availability that aligns with your healthcare needs.',
    category: 'Specialists',
  },
  {
    question: 'Are the doctors verified?',
    answer:
      'Yes. Specialists are selected based on their qualifications, experience, and professional credentials to ensure safe and responsible medical guidance.',
    category: 'Specialists',
  },
  {
    question: 'Can I request a second opinion?',
    answer:
      'Yes. If you would like confirmation or additional clarity regarding a diagnosis or treatment plan, you can request a structured second opinion from a relevant specialist.',
    category: 'Specialists',
  },
  {
    question: 'Do specialists provide prescriptions?',
    answer:
      'Prescriptions are issued only when clinically appropriate and permitted under applicable regulations.',
    category: 'Specialists',
  },
  {
    question: 'What type of medical concerns can be addressed?',
    answer:
      'Globe Medical supports non-emergency health concerns, follow-up care, specialist reviews, chronic condition guidance, and medical second opinions.',
    category: 'Care',
  },
  {
    question: 'Is this service suitable for urgent emergencies?',
    answer:
      'No. Globe Medical is not designed for emergency care. If you experience severe symptoms such as chest pain, breathing difficulty, or loss of consciousness, seek immediate in-person emergency services.',
    category: 'Care',
    warning: true,
  },
  {
    question: 'What happens after my consultation?',
    answer:
      'After your session, you will receive documented guidance, recommended next steps, and follow-up planning inside your digital care workspace.',
    category: 'Care',
  },
  {
    question: 'Is Globe Medical suitable for long-term health conditions?',
    answer:
      'Yes. The platform supports ongoing follow-up, progress monitoring, and structured guidance for chronic and recurring conditions.',
    category: 'Care',
  },
  {
    question: 'How are my medical records managed?',
    answer:
      'All uploaded documents, consultation notes, and recommendations are securely stored within your private digital care workspace and accessible only to authorised healthcare professionals.',
    category: 'Privacy',
  },
  {
    question: 'How is my data protected?',
    answer:
      'Globe Medical operates on a secure, privacy-first digital infrastructure to safeguard your personal health information.',
    category: 'Privacy',
  },
];

const categoryColors = {
  General: 'bg-[#1e3a8a]/8 text-[#1e3a8a]',
  Specialists: 'bg-[#0e7490]/10 text-[#0e7490]',
  Care: 'bg-emerald-50 text-emerald-700',
  Privacy: 'bg-violet-50 text-violet-700',
};

const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <div
    className={`group border rounded-2xl overflow-hidden transition-all duration-300 ${
      isOpen
        ? 'border-[#1e3a8a]/25 shadow-md shadow-[#1e3a8a]/8'
        : 'border-gray-100 hover:border-[#1e3a8a]/20 hover:shadow-sm'
    } bg-white`}
  >
    <button
      className="w-full text-left px-4 sm:px-6 py-5 flex items-start gap-4 min-h-[60px]"
      onClick={() => onToggle(index)}
    >
      {/* Toggle icon */}
      <span
        className={`flex-shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white shadow-md'
            : 'bg-gray-100 text-gray-400 group-hover:bg-[#1e3a8a]/10 group-hover:text-[#1e3a8a]'
        }`}
      >
        {isOpen ? (
          <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
        ) : (
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
        )}
      </span>

      <div className="flex-1 flex flex-wrap items-center gap-3">
        <span
          className={`text-[0.65rem] sm:text-[0.7rem] font-semibold uppercase tracking-widest px-2.5 py-0.5 rounded-full ${categoryColors[faq.category]}`}
        >
          {faq.category}
        </span>
        <span
          className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
            isOpen ? 'text-[#1e3a8a]' : 'text-[#0f172a]'
          }`}
        >
          {faq.question}
        </span>
      </div>
    </button>

    {/* Answer panel */}
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-[300px] sm:max-h-[400px] lg:max-h-60 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="pl-[4.25rem] pr-6 pb-5">
        {faq.warning && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg mb-3 w-fit">
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
            For emergencies, call local emergency services immediately.
          </div>
        )}
        <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
      </div>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Group FAQs by category
  const generalFaqs = faqs.filter((faq) => faq.category === 'General');
  const otherFaqs = faqs.filter((faq) => faq.category !== 'General');

  return (
    <section className="relative bg-[#f8fafc] py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1e3a8a]/15 to-transparent" />
        <div className="absolute -top-60 -right-60 w-[600px] h-[600px] bg-[#22d3ee]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-60 -left-60 w-[600px] h-[600px] bg-[#1e3a8a]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] leading-tight mb-4">
            Frequently Asked
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1e3a8a] to-[#22d3ee]">
              Questions
            </span>
          </h2>
          <p className="max-w-xl mx-auto text-gray-500 text-sm sm:text-base leading-relaxed">
            Everything you need to know about Globe Medical and how it works.
          </p>

          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#1e3a8a]/30" />
            <div className="w-2 h-2 rounded-full bg-[#22d3ee]" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#1e3a8a]/30" />
          </div>
        </div>

        {/* General FAQs Section */}
        <div className="mb-16">
          <h3 className="text-xl sm:text-2xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
            <span className="w-1.5 h-8 bg-gradient-to-b from-[#1e3a8a] to-[#22d3ee] rounded-full"></span>
            General
          </h3>
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
            {generalFaqs.map((faq) => {
              const globalIndex = faqs.indexOf(faq);
              return (
                <FAQItem
                  key={globalIndex}
                  faq={faq}
                  index={globalIndex}
                  isOpen={openIndex === globalIndex}
                  onToggle={handleToggle}
                />
              );
            })}
          </div>
        </div>

        {/* Specialists, Care & Privacy FAQs Section */}
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
            <span className="w-1.5 h-8 bg-gradient-to-b from-[#0e7490] to-[#22d3ee] rounded-full"></span>
            Specialists, Care & Privacy
          </h3>
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
            {otherFaqs.map((faq) => {
              const globalIndex = faqs.indexOf(faq);
              return (
                <FAQItem
                  key={globalIndex}
                  faq={faq}
                  index={globalIndex}
                  isOpen={openIndex === globalIndex}
                  onToggle={handleToggle}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
